// Imported by its own path rather than off the utils barrel, which drags the whole app graph in
// and cycles under jest - the same reason the budget's testable helpers avoid it.
import { roundToCents } from 'utils/roundToCents'

/**
 * A charge's interest-from date is stored as YYYY-MM-DD, and the year has to be one somebody meant:
 * a native date input turns a typed 8/26/26 into 0026-08-26, which is well-formed and two thousand
 * years of daily interest. The same window the server accrues over and InputValidate accepts, so
 * the field errors rather than this quietly accruing from a year nobody typed.
 */
export const YMD = /^(19|20|21)\d{2}-\d{1,2}-\d{1,2}$/

/** Whole days from an interest-from date through `asOf` (default today), in UTC to avoid timezone drift. */
export const daysSince = (fromYmd: string, asOf: Date = new Date()): number => {
  if (!YMD.test(fromYmd)) return 0
  const [y, m, d] = fromYmd.split('-').map(Number)
  const today = Date.UTC(asOf.getFullYear(), asOf.getMonth(), asOf.getDate())
  // Date.UTC maps a year under 100 into the 1900s, which is how a from-date of 0026 came to read
  // as a hundred years of accrual rather than as the nonsense it is; the year check above is what
  // keeps such a year from reaching here at all.
  return Math.floor((today - Date.UTC(y, m - 1, d)) / 86400000)
}

/**
 * Interest accrued on a charge as of `asOf` (default today): actual days on the unpaid balance from
 * its interest-from date at its own rate, over `denominator` - the loan's Daily Rate Basis, resolved
 * by `dailyRateDenominator`, and NOT a fixed 360. No rate (or no valid from-date) means nothing
 * accrues. Mirrors the server's `LoanChargeService.accruedInterestFor` so the Add/Update Loan Charge
 * modal previews what the server will store.
 *
 * The 360 the modal used to divide by regardless is invisible on a 360-basis loan (longhorn, which
 * is where charge interest gets looked at) and overstates the interest by 365/360 - about 1.4% - on
 * a 365-basis one. It is also what a basis nobody ever set resolves to, so the tenants it was wrong
 * for were the ones least likely to have noticed.
 *
 * Rounded to the cent, as the server rounds it: interest on a charge is money owed on that charge.
 * Left unrounded, the fraction of a cent daily interest always carries reached the `thousandSep`
 * field, which TRUNCATES its third decimal rather than rounding it - $257.54 at 18% for 68 days is
 * $8.75636, shown as $8.75 against the $8.76 the server stores and the prior servicer demands
 * (Smartsheet row 475).
 */
export const chargeAccruedInterest = (
  unpaidBalance: number,
  interestRate: number,
  interestFromDate: string,
  denominator: number,
  asOf?: Date,
): number => {
  const days = daysSince(interestFromDate, asOf)
  if (interestRate && days > 0) return roundToCents((unpaidBalance * interestRate * days) / denominator / 100)
  return 0
}
